/** Plan / credit helpers for product menu + feature-preview gating. */

export const WEBAPP_CONNECTOR = "webapp-connector";

export type IncludedAppLike = {
  appIdentifier: string;
  unlimited?: boolean;
  includedCredits?: number;
};

export type AppAccessAccount = {
  subscription?: {
    plan?: {
      includedApps?: IncludedAppLike[];
      features?: string[];
    };
  };
  appCreditBalances?: Record<string, number>;
  appBonusCreditBalances?: Record<string, number>;
} | null | undefined;

const norm = (value: string) => value.toLowerCase();

export function hasPlanConnector(
  account: AppAccessAccount,
  connector: string,
): boolean {
  const features = account?.subscription?.plan?.features ?? [];
  const target = norm(connector);
  return features.some((f) => norm(f) === target);
}

export function findIncludedApp(
  account: AppAccessAccount,
  appIdentifier: string,
): IncludedAppLike | undefined {
  const apps = account?.subscription?.plan?.includedApps ?? [];
  const target = norm(appIdentifier);
  return apps.find((app) => norm(app.appIdentifier) === target);
}

function balanceFor(
  balances: Record<string, number> | undefined,
  appIdentifier: string,
): number {
  if (!balances) return 0;
  if (typeof balances[appIdentifier] === "number") {
    return balances[appIdentifier];
  }
  const target = norm(appIdentifier);
  for (const [key, value] of Object.entries(balances)) {
    if (norm(key) === target && typeof value === "number") return value;
  }
  return 0;
}

/** Window pool + bonus for one app. */
export function getAppRemainingCredits(
  account: AppAccessAccount,
  appIdentifier: string,
): number {
  return (
    balanceFor(account?.appCreditBalances, appIdentifier) +
    balanceFor(account?.appBonusCreditBalances, appIdentifier)
  );
}

/**
 * True when the app is on the plan and has usable credits
 * (unlimited flag or remaining window/bonus > 0).
 */
export function canUseApp(
  account: AppAccessAccount,
  appIdentifier: string,
): boolean {
  const app = findIncludedApp(account, appIdentifier);
  if (!app) return false;
  if (app.unlimited) return true;
  return getAppRemainingCredits(account, appIdentifier) > 0;
}
